// js/views/dashboard.js
import { getMySites, deleteSite, publishSite, staticFetch, TEMPLATES_BASE_URL } from "../api.js";

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

const joinBaseUrl = (baseUrl, assetPath) => {
    const rawPath = String(assetPath || "").trim();
    if (!rawPath) return "";
    if (isAbsoluteUrl(rawPath)) return rawPath;

    const cleanBase = String(baseUrl || "").replace(/\/+$/, "");
    const cleanPath = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
    return `${cleanBase}${cleanPath}`;
};

const appendCacheBust = (url, cacheBust) => {
    if (!url) return "";
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}v=${encodeURIComponent(cacheBust)}`;
};

const buildPublishedSiteUrl = (sitePath, storedUrl = "") => {
    const safePath = encodeURIComponent(String(sitePath || "").trim());
    const pathUrl = `${window.location.origin}/${safePath}`;
    const trimmedStoredUrl = String(storedUrl || "").trim();

    if (trimmedStoredUrl) {
        try {
            const parsed = new URL(trimmedStoredUrl, window.location.origin);
            const normalizedPath = parsed.pathname.replace(/\/+$/, "");
            if (normalizedPath && normalizedPath !== "/") {
                return parsed.toString();
            }
        } catch {
            // ignore malformed stored URL and rebuild from slug
        }
    }

    return pathUrl;
};

const buildDraftPreviewUrl = (sitePath) =>
    `${window.location.origin}/preview/${encodeURIComponent(String(sitePath || "").trim())}`;

const normalizeTemplate = (tpl, cacheBust) => {
    const category = tpl?.categoria || tpl?.category || tpl?.manifest?.category || "Geral";
    const vertical =
        tpl?.vertical ||
        tpl?.name ||
        tpl?.title ||
        tpl?.manifest?.name ||
        tpl?.manifest?.title ||
        "Template";
    const thumbnailSource =
        tpl?.catalogImage ||
        tpl?.thumbnail_url ||
        tpl?.thumbnail ||
        tpl?.preview?.thumbnail ||
        "";

    return {
        category,
        vertical,
        templateId: `${category}/${vertical}`,
        thumbUrl: appendCacheBust(
            thumbnailSource
                ? joinBaseUrl(TEMPLATES_BASE_URL, thumbnailSource)
                : "",
            cacheBust
        ),
    };
};

const slugifyTemplateName = (value) =>
    String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

const matchSiteToTemplate = (site, templates) => {
    const templateId = String(site?.template_id || "").trim();
    if (!templateId) return null;

    return (
        templates.find((template) => template.templateId === templateId) ||
        templates.find((template) => template.vertical === templateId) ||
        templates.find((template) => slugifyTemplateName(template.vertical) === templateId)
    );
};

const buildFallbackCover = (title) =>
    `https://placehold.co/1200x720/e2e8f0/0f172a?text=${encodeURIComponent(title || "SiteEiro")}`;

export const DashboardView = {
    render: async () => {
        const token = localStorage.getItem("heyweb_token");

        if (!token) {
            window.location.hash = "#login";
            return '<div style="padding:2rem; text-align:center;">Redirecionando...</div>';
        }

        let sitesHtml = "";

        try {
            const cacheBust = Date.now();
            const [sites, catalogResponse] = await Promise.all([
                getMySites(),
                staticFetch(`/templates.json?v=${cacheBust}`, { method: "GET" }),
            ]);

            const templatesCatalog = catalogResponse.ok ? await catalogResponse.json() : [];
            const normalizedTemplates = Array.isArray(templatesCatalog)
                ? templatesCatalog.map((tpl) => normalizeTemplate(tpl, cacheBust))
                : [];

            if (Array.isArray(sites) && sites.length > 0) {
                sitesHtml = sites
                    .map((site) => {
                        const safePath = site?.path || "";
                        const safeName = site?.name || "Sem titulo";
                        const previewUrl = buildDraftPreviewUrl(safePath);
                        const publicUrl = buildPublishedSiteUrl(safePath, site?.url);
                        const publishLabel = site?.published ? "Abrir Publicado" : "Publicar Site";
                        const statusLabel = site?.published ? "Publicado" : "Rascunho";
                        const statusStyles = site?.published
                            ? "background:rgba(22, 163, 74, 0.12); color:#166534; border-color:rgba(22,163,74,0.2);"
                            : "background:rgba(245, 158, 11, 0.12); color:#92400e; border-color:rgba(245,158,11,0.18);";
                        const matchedTemplate = matchSiteToTemplate(site, normalizedTemplates);
                        const templateBadge = matchedTemplate?.category || "Template";
                        const coverUrl = matchedTemplate?.thumbUrl || buildFallbackCover(safeName);
                        const siteSubtitle =
                            site?.hero_subtitle ||
                            site?.hero_title ||
                            site?.extra?.hero_subtitle ||
                            "Projeto pronto para editar, visualizar e publicar.";

                        return `
              <article class="site-card animate-fade-in"
                style="background:rgba(255,255,255,0.92); border:1px solid rgba(226,232,240,0.95); border-radius:1.5rem; overflow:hidden; box-shadow:0 24px 50px rgba(15,23,42,0.08); display:flex; flex-direction:column;">
                <div style="position:relative; min-height:220px; background:#e2e8f0; overflow:hidden;">
                  <img src="${coverUrl}" alt="${safeName}" style="display:block; width:100%; height:220px; object-fit:cover;">
                  <div style="position:absolute; inset:0; background:linear-gradient(180deg, rgba(15,23,42,0.04) 0%, rgba(15,23,42,0.72) 100%);"></div>
                  <div style="position:absolute; inset:0; padding:1.1rem; display:flex; flex-direction:column; justify-content:space-between;">
                    <div style="display:flex; align-items:flex-start; justify-content:space-between; gap:0.75rem;">
                      <span style="display:inline-flex; align-items:center; gap:0.35rem; padding:0.45rem 0.7rem; border-radius:999px; background:rgba(255,255,255,0.88); color:#0f172a; font-size:0.72rem; font-weight:800; letter-spacing:0.04em; text-transform:uppercase; box-shadow:0 8px 24px rgba(15,23,42,0.08);">
                        ${templateBadge}
                      </span>
                      <span style="${statusStyles} display:inline-flex; align-items:center; padding:0.45rem 0.7rem; border-radius:999px; border:1px solid transparent; font-size:0.72rem; font-weight:800; letter-spacing:0.04em; text-transform:uppercase; backdrop-filter:blur(10px);">
                        ${statusLabel}
                      </span>
                    </div>

                    <div>
                      <p style="margin:0 0 0.35rem; color:rgba(255,255,255,0.82); font-size:0.76rem; font-weight:700; letter-spacing:0.08em; text-transform:uppercase;">
                        /${safePath}
                      </p>
                      <h3 style="margin:0; color:white; font-size:1.35rem; font-weight:900; line-height:1.15; letter-spacing:-0.03em;">
                        ${safeName}
                      </h3>
                    </div>
                  </div>
                </div>

                <div style="padding:1.25rem 1.25rem 1.35rem; display:grid; gap:1rem;">
                  <p style="margin:0; color:#64748b; font-size:0.92rem; line-height:1.55; min-height:2.8rem;">
                    ${siteSubtitle}
                  </p>

                  <div style="padding:0.9rem 1rem; border:1px solid #e2e8f0; border-radius:1rem; background:linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);">
                    <div style="font-size:0.68rem; color:#94a3b8; font-weight:800; text-transform:uppercase; letter-spacing:0.08em; margin-bottom:0.35rem;">
                      Link publicado
                    </div>
                    <div style="font-size:0.84rem; color:#334155; word-break:break-all; font-weight:600;">
                      ${publicUrl}
                    </div>
                  </div>

                  <div style="display:grid; grid-template-columns:minmax(0,1fr) auto; gap:0.75rem; align-items:center;">
                    <a href="${previewUrl}" target="_blank" rel="noopener noreferrer"
                      style="display:inline-flex; align-items:center; justify-content:center; min-height:3rem; padding:0 1rem; border-radius:0.95rem; background:#3b82f6; color:white; text-decoration:none; font-size:0.92rem; font-weight:800; box-shadow:0 14px 28px rgba(59,130,246,0.24);">
                      Visualizar Site
                    </a>
                    <button class="btn-publish-site" data-site-path="${safePath}" data-site-url="${encodeURIComponent(publicUrl)}" data-site-published="${site?.published ? "1" : "0"}"
                      style="display:inline-flex; align-items:center; justify-content:center; min-height:3rem; padding:0 1rem; border-radius:0.95rem; border:none; background:#0f172a; color:white; font-size:0.9rem; font-weight:900; cursor:pointer; white-space:nowrap;">
                      ${publishLabel}
                    </button>
                  </div>

                  <div style="display:grid; grid-template-columns:1fr auto; gap:0.75rem; align-items:center; border-top:1px solid #eef2f7; padding-top:0.95rem;">
                    <button class="btn-edit-site" data-site-path="${safePath}"
                      style="display:inline-flex; align-items:center; justify-content:center; min-height:2.9rem; padding:0 1rem; border-radius:0.9rem; border:1px solid #dbe4f0; background:white; color:#0f172a; font-size:0.9rem; font-weight:800; cursor:pointer;">
                      Editar Projeto
                    </button>
                    <button class="btn-delete-site" data-site-path="${safePath}" data-site-name="${encodeURIComponent(safeName)}"
                      style="display:inline-flex; align-items:center; justify-content:center; min-height:2.9rem; width:2.9rem; border-radius:0.9rem; border:1px solid #fecdd3; background:#fff1f2; color:#e11d48; font-size:1rem; font-weight:900; cursor:pointer;"
                      aria-label="Eliminar ${safeName}" title="Eliminar ${safeName}">
                      <span aria-hidden="true">×</span>
                    </button>
                  </div>
                </div>
              </article>
            `;
                    })
                    .join("");
            } else {
                sitesHtml = `
          <div style="grid-column:1 / -1; text-align:center; padding:4rem 2rem; color:#94a3b8; border:2px dashed #e2e8f0; border-radius:1.5rem; background:#f8fafc;">
            <div style="font-size:3rem; margin-bottom:1rem;">+</div>
            <p style="font-weight:800; color:#475569; font-size:1.1rem; margin-bottom:0.5rem;">O teu dashboard esta pronto.</p>
            <p style="margin:0; font-size:0.9rem;">Cria o teu primeiro site para ele aparecer aqui.</p>
          </div>
        `;
            }
        } catch (error) {
            console.error("ERRO DASHBOARD:", error);
            sitesHtml = `
        <div style="grid-column:1 / -1; padding:2rem; background:#fff1f2; border:1px solid #fda4af; border-radius:1rem; color:#9f1239; text-align:center;">
          <h3 style="margin:0 0 0.5rem 0; font-weight:900;">Nao foi possivel carregar os sites</h3>
          <p style="margin:0; font-size:0.85rem;">Detalhe: ${error.message}</p>
          <button onclick="window.location.reload()" style="margin-top:1rem; background:#be123c; color:white; border:none; padding:0.5rem 1rem; border-radius:0.4rem; cursor:pointer; font-weight:700;">Tentar Novamente</button>
        </div>
      `;
        }

        return `
      <section class="animate-fade-in" style="max-width:1280px; margin:0 auto; padding:2rem 1rem;">
        <header style="display:flex; justify-content:space-between; align-items:flex-end; gap:1rem; margin-bottom:2rem; flex-wrap:wrap;">
          <div>
            <h1 style="margin:0; font-size:2.25rem; font-weight:900; color:#0f172a; letter-spacing:-0.025em;">Os Meus Projetos</h1>
            <p style="color:#64748b; margin-top:0.5rem; font-size:1rem;">Gerencia e edita os teus websites em tempo real.</p>
          </div>

          <button onclick="window.location.hash = '#templates'"
            style="background:#3b82f6; color:white; border:none; padding:0.85rem 1.45rem; border-radius:0.95rem; font-weight:900; cursor:pointer; box-shadow:0 14px 28px rgba(59,130,246,0.24); transition:transform 0.2s;">
            + Criar Novo Site
          </button>
        </header>

        <div style="display:grid; grid-template-columns:repeat(auto-fill, minmax(360px, 1fr)); gap:1.75rem;">
          <div class="add-site-card" onclick="window.location.hash = '#templates'"
            style="border:1.5px dashed #cbd5e1; border-radius:1.5rem; padding:2rem; display:flex; flex-direction:column; align-items:center; justify-content:center; cursor:pointer; background:linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,250,252,0.78) 100%); min-height:280px; transition:all 0.3s;">
            <div style="background:white; color:#3b82f6; width:64px; height:64px; border-radius:1.1rem; display:flex; align-items:center; justify-content:center; margin-bottom:1.25rem; font-size:2rem; font-weight:300; border:1px solid #e2e8f0; box-shadow:0 10px 24px rgba(148,163,184,0.14);">
              +
            </div>
            <span style="font-weight:900; color:#334155; font-size:1.05rem;">Novo Projeto</span>
            <p style="margin:0.65rem 0 0; color:#94a3b8; font-size:0.9rem; text-align:center; max-width:16rem; line-height:1.5;">
              Escolhe um template e arranca com um novo site a partir do catalogo.
            </p>
          </div>

          ${sitesHtml}
        </div>
      </section>
    `;
    },

    afterRender: async () => {
        if (window.lucide) window.lucide.createIcons();

        document.querySelectorAll(".btn-edit-site").forEach((btn) => {
            btn.onclick = () => {
                const path = btn.getAttribute("data-site-path");
                if (!path) return alert("Erro: Identificador do site nao encontrado.");
                window.location.hash = `#editor/site/${encodeURIComponent(path)}`;
            };
        });

        document.querySelectorAll(".btn-delete-site").forEach((btn) => {
            btn.onclick = async () => {
                const path = btn.getAttribute("data-site-path");
                const name = decodeURIComponent(btn.getAttribute("data-site-name") || "este site");
                if (!path) return;

                if (!confirm(`ATENCAO: Tens a certeza que queres eliminar o site "${name}"?\nEsta acao nao pode ser revertida.`)) {
                    return;
                }

                const originalContent = btn.innerHTML;
                btn.innerHTML = "…";
                btn.disabled = true;

                try {
                    const result = await deleteSite(path);

                    if (Array.isArray(result?.warnings) && result.warnings.length) {
                        alert(`Site eliminado do CMS e da base de dados.\nAviso R2: ${result.warnings.join(" | ")}`);
                    }

                    window.location.reload();
                } catch (e) {
                    alert(`Erro ao eliminar: ${e.message}`);
                } finally {
                    btn.disabled = false;
                    btn.innerHTML = originalContent;
                }
            };
        });

        document.querySelectorAll(".btn-publish-site").forEach((btn) => {
            btn.onclick = async () => {
                const path = btn.getAttribute("data-site-path");
                const publicUrl = decodeURIComponent(btn.getAttribute("data-site-url") || "");
                const isPublished = btn.getAttribute("data-site-published") === "1";
                if (!path) return;

                if (isPublished && publicUrl) {
                    window.open(publicUrl, "_blank", "noopener,noreferrer");
                    return;
                }

                const originalContent = btn.innerText;
                btn.innerText = "A publicar...";
                btn.disabled = true;

                try {
                    const published = await publishSite(path);
                    const nextUrl = buildPublishedSiteUrl(path, published?.url || publicUrl);
                    alert(`Site publicado com sucesso em ${nextUrl}`);
                    window.open(nextUrl, "_blank", "noopener,noreferrer");
                    window.location.reload();
                } catch (error) {
                    alert(error.message || "Nao foi possivel publicar o site.");
                    btn.disabled = false;
                    btn.innerText = originalContent;
                }
            };
        });
    },
};
